import { useState } from "react";
import { Card } from "@mui/material";
import { useTranslation } from "react-i18next";

import MDBox from "@vendor/components/MDBox";
import MDTypography from "@vendor/components/MDTypography";
import { MainLayout } from "@shared/ui/MainLayout";
import { STORAGE_KEYS } from "@shared/constants/storageKeys";
import type { UserIdentity } from "@shared/hooks/useUserData";

const SUPPORT_FORM_URL =
  "https://forms.zohopublic.com/dominionms/form/Complaints/formperma/QQL1n9lD8qkjwdaefdSu2MjVZSXOsA_naOE4Fv6O96M";

/**
 * Zoho Forms prellena campos desde la query string. Las claves son los nombres de campo
 * definidos en el formulario de Zoho: si alguien los renombra allá, el prellenado deja de
 * funcionar sin error y el colaborador vuelve a ver los campos vacíos.
 *
 * El usuario sale de localStorage, así que un registro viejo o incompleto puede traer campos
 * nulos o vacíos. Esos se omiten: URLSearchParams los escribiría como "undefined" o "null" y
 * el formulario mostraría ese texto en lugar de dejar el campo en blanco.
 */
const buildSupportFormUrl = (user: UserIdentity | null): string => {
  if (!user) return SUPPORT_FORM_URL;
  const fields: Record<string, unknown> = {
    Email: user.email,
    Name_First: user.name,
    Name_Last: user.surname,
  };
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    if (typeof value === "string" && value.trim() !== "") params.append(key, value.trim());
  }
  const query = params.toString();
  return query ? `${SUPPORT_FORM_URL}?${query}` : SUPPORT_FORM_URL;
};

/**
 * Se lee localStorage de forma síncrona en vez de usar useUserData: ese hook entrega el
 * usuario recién después del primer render, y el iframe alcanzaba a pedir el formulario vacío
 * antes de recargarlo con los datos (dos requests a Zoho y un parpadeo, también en producción).
 */
const readStoredUser = (): UserIdentity | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER);
    return stored ? (JSON.parse(stored) as UserIdentity) : null;
  } catch {
    return null;
  }
};

export const SupportPage = () => {
  const { t } = useTranslation();
  const [user] = useState(readStoredUser);

  return (
    <MainLayout>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h4" fontWeight="medium" mb={2}>
              {t("supportDescription")}
            </MDTypography>
            <MDBox
              component="iframe"
              aria-label="Solicitud para solicitar soporte"
              frameBorder="0"
              sx={{ height: "500px", width: "100%", border: "none" }}
              src={buildSupportFormUrl(user)}
            />
          </MDBox>
        </Card>
      </MDBox>
    </MainLayout>
  );
};
